import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";  // Use shared api.js with auto-switching

export default function CompanyProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [message, setMessage] = useState({ type: "", text: "" });

  const [profile, setProfile] = useState({
    // Basic Information
    companyName: "",
    tagLine: "",
    shortName: "",

    // Contact Information
    email: "",
    supportEmail: "",
    phone: "",
    alternatePhone: "",
    whatsApp: "",

    // Address Information
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",

    // Registration & Legal
    registrationNumber: "",
    taxId: "",

    // Social Media
    website: "",
    facebook: "",
    twitter: "",
    instagram: "",
    linkedIn: "",

    // Branding
    logoUrl: "",
    faviconUrl: "",
    primaryColor: "#1a1a2e",
    secondaryColor: "#007bff",

    // Banking Details
    bankName: "",
    bankAccountNumber: "",
    bankAccountName: "",
    currency: "NGN",
    currencySymbol: "₦",

    // Operating Hours
    businessHours: "",
    supportHours: "",

    // Additional Information
    aboutUs: "",
    mission: "",
    vision: "",

    // Document Footers
    statementFooter: "",
    receiptFooter: ""
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get("/CompanyProfile");
      setProfile(response.data);
    } catch (error) {
      console.error("Error loading profile:", error);
      setMessage({ type: "error", text: "Failed to load company profile" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      await api.put("/CompanyProfile", profile);
      setMessage({ type: "success", text: "Company profile updated successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error saving profile:", error);
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to save profile" });
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: "basic", label: "🏢 Basic Info", icon: "🏢" },
    { id: "contact", label: "📞 Contact", icon: "📞" },
    { id: "address", label: "📍 Address", icon: "📍" },
    { id: "legal", label: "📋 Legal", icon: "📋" },
    { id: "social", label: "🌐 Social Media", icon: "🌐" },
    { id: "branding", label: "🎨 Branding", icon: "🎨" },
    { id: "banking", label: "🏦 Banking", icon: "🏦" },
    { id: "hours", label: "🕐 Hours", icon: "🕐" },
    { id: "about", label: "ℹ️ About", icon: "ℹ️" },
    { id: "documents", label: "📄 Documents", icon: "📄" }
  ];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading company profile...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🏢 Company Profile</h1>
          <p style={styles.subtitle}>Manage your company information</p>
        </div>
        <button onClick={() => navigate("/admin")} style={styles.backButton}>
          ← Back to Admin
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
          color: message.type === "success" ? "#155724" : "#721c24",
          borderColor: message.type === "success" ? "#c3e6cb" : "#f5c6cb"
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.content}>
        {/* Sidebar Navigation */}
        <div style={styles.sidebar}>
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              style={{
                ...styles.sidebarItem,
                ...(activeSection === section.id ? styles.sidebarItemActive : {})
              }}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div style={styles.main}>
          {/* Basic Information */}
          {activeSection === "basic" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>🏢 Basic Information</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Company Name *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={profile.companyName}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Enter company name"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Short Name</label>
                  <input
                    type="text"
                    name="shortName"
                    value={profile.shortName || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Uptech"
                  />
                </div>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Tagline / Slogan</label>
                  <input
                    type="text"
                    name="tagLine"
                    value={profile.tagLine || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Digital Banking Solutions"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Contact Information */}
          {activeSection === "contact" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>📞 Contact Information</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Primary Email</label>
                  <input
                    type="email"
                    name="email"
                    value={profile.email || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="info@company.com"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Support Email</label>
                  <input
                    type="email"
                    name="supportEmail"
                    value={profile.supportEmail || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="support@company.com"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Primary Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="+234 xxx xxx xxxx"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Alternate Phone</label>
                  <input
                    type="tel"
                    name="alternatePhone"
                    value={profile.alternatePhone || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="+234 xxx xxx xxxx"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>WhatsApp Number</label>
                  <input
                    type="tel"
                    name="whatsApp"
                    value={profile.whatsApp || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="+234 xxx xxx xxxx"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Address Information */}
          {activeSection === "address" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>📍 Address Information</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Street Address</label>
                  <textarea
                    name="address"
                    value={profile.address || ""}
                    onChange={handleChange}
                    style={{ ...styles.input, minHeight: "80px" }}
                    placeholder="Enter full street address"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={profile.city || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Lagos"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>State / Region</label>
                  <input
                    type="text"
                    name="state"
                    value={profile.state || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Lagos State"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={profile.country || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Nigeria"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={profile.postalCode || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., 100001"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Legal Information */}
          {activeSection === "legal" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>📋 Registration & Legal</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Registration Number (RC)</label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={profile.registrationNumber || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., RC 123456"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tax ID (TIN)</label>
                  <input
                    type="text"
                    name="taxId"
                    value={profile.taxId || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., 12345678-0001"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Social Media */}
          {activeSection === "social" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>🌐 Social Media & Website</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Website URL</label>
                  <input
                    type="url"
                    name="website"
                    value={profile.website || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://www.yourcompany.com"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Facebook</label>
                  <input
                    type="url"
                    name="facebook"
                    value={profile.facebook || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Twitter / X</label>
                  <input
                    type="url"
                    name="twitter"
                    value={profile.twitter || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://twitter.com/yourhandle"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Instagram</label>
                  <input
                    type="url"
                    name="instagram"
                    value={profile.instagram || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>LinkedIn</label>
                  <input
                    type="url"
                    name="linkedIn"
                    value={profile.linkedIn || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://linkedin.com/company/yourcompany"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Branding */}
          {activeSection === "branding" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>🎨 Branding</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Logo URL</label>
                  <input
                    type="url"
                    name="logoUrl"
                    value={profile.logoUrl || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://example.com/logo.png"
                  />
                  {profile.logoUrl && (
                    <div style={styles.logoPreview}>
                      <img src={profile.logoUrl} alt="Logo Preview" style={styles.logoImage} />
                    </div>
                  )}
                </div>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Favicon URL</label>
                  <input
                    type="url"
                    name="faviconUrl"
                    value={profile.faviconUrl || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://example.com/favicon.ico"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Primary Color</label>
                  <div style={styles.colorInputWrapper}>
                    <input
                      type="color"
                      name="primaryColor"
                      value={profile.primaryColor || "#1a1a2e"}
                      onChange={handleChange}
                      style={styles.colorInput}
                    />
                    <input
                      type="text"
                      value={profile.primaryColor || "#1a1a2e"}
                      onChange={(e) => setProfile(prev => ({ ...prev, primaryColor: e.target.value }))}
                      style={styles.colorTextInput}
                    />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Secondary Color</label>
                  <div style={styles.colorInputWrapper}>
                    <input
                      type="color"
                      name="secondaryColor"
                      value={profile.secondaryColor || "#007bff"}
                      onChange={handleChange}
                      style={styles.colorInput}
                    />
                    <input
                      type="text"
                      value={profile.secondaryColor || "#007bff"}
                      onChange={(e) => setProfile(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      style={styles.colorTextInput}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Banking Details */}
          {activeSection === "banking" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>🏦 Banking Details</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    value={profile.bankName || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., First Bank"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Account Number</label>
                  <input
                    type="text"
                    name="bankAccountNumber"
                    value={profile.bankAccountNumber || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., 1234567890"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Account Name</label>
                  <input
                    type="text"
                    name="bankAccountName"
                    value={profile.bankAccountName || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Uptech Limited"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Currency Code</label>
                  <input
                    type="text"
                    name="currency"
                    value={profile.currency || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., NGN"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Currency Symbol</label>
                  <input
                    type="text"
                    name="currencySymbol"
                    value={profile.currencySymbol || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., ₦"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Operating Hours */}
          {activeSection === "hours" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>🕐 Operating Hours</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Business Hours</label>
                  <input
                    type="text"
                    name="businessHours"
                    value={profile.businessHours || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., Mon - Fri: 8:00 AM - 5:00 PM"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Support Hours</label>
                  <input
                    type="text"
                    name="supportHours"
                    value={profile.supportHours || ""}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="e.g., 24/7"
                  />
                </div>
              </div>
            </div>
          )}

          {/* About Information */}
          {activeSection === "about" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>ℹ️ About Company</h2>
              <div style={styles.formGrid}>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>About Us</label>
                  <textarea
                    name="aboutUs"
                    value={profile.aboutUs || ""}
                    onChange={handleChange}
                    style={{ ...styles.input, minHeight: "120px" }}
                    placeholder="Brief description about your company..."
                  />
                </div>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Mission Statement</label>
                  <textarea
                    name="mission"
                    value={profile.mission || ""}
                    onChange={handleChange}
                    style={{ ...styles.input, minHeight: "80px" }}
                    placeholder="Our mission is..."
                  />
                </div>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Vision Statement</label>
                  <textarea
                    name="vision"
                    value={profile.vision || ""}
                    onChange={handleChange}
                    style={{ ...styles.input, minHeight: "80px" }}
                    placeholder="Our vision is..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Document Settings */}
          {activeSection === "documents" && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>📄 Document Settings</h2>
              <p style={styles.sectionDesc}>
                These texts will appear on generated statements, receipts, and other documents.
              </p>
              <div style={styles.formGrid}>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Statement Footer Text</label>
                  <textarea
                    name="statementFooter"
                    value={profile.statementFooter || ""}
                    onChange={handleChange}
                    style={{ ...styles.input, minHeight: "80px" }}
                    placeholder="e.g., This is a computer-generated statement and does not require a signature."
                  />
                </div>
                <div style={styles.formGroupFull}>
                  <label style={styles.label}>Receipt Footer Text</label>
                  <textarea
                    name="receiptFooter"
                    value={profile.receiptFooter || ""}
                    onChange={handleChange}
                    style={{ ...styles.input, minHeight: "80px" }}
                    placeholder="e.g., Thank you for banking with us."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div style={styles.saveSection}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={styles.saveButton}
            >
              {saving ? "Saving..." : "💾 Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f7fa",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: "15px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e9ecef",
    borderTopColor: "#007bff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  header: {
    backgroundColor: "#1a1a2e",
    color: "white",
    padding: "25px 30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "600"
  },
  subtitle: {
    margin: "5px 0 0",
    fontSize: "14px",
    opacity: 0.8
  },
  backButton: {
    padding: "10px 20px",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "white",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px"
  },
  message: {
    padding: "15px 30px",
    borderWidth: "1px",
    borderStyle: "solid",
    fontSize: "14px"
  },
  content: {
    display: "flex",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "30px"
  },
  sidebar: {
    width: "220px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "5px"
  },
  sidebarItem: {
    padding: "12px 16px",
    backgroundColor: "white",
    border: "1px solid #e9ecef",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    textAlign: "left",
    transition: "all 0.2s"
  },
  sidebarItemActive: {
    backgroundColor: "#007bff",
    color: "white",
    borderColor: "#007bff"
  },
  main: {
    flex: 1,
    marginLeft: "30px"
  },
  section: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "30px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
  },
  sectionTitle: {
    margin: "0 0 10px",
    fontSize: "20px",
    fontWeight: "600",
    color: "#1a1a2e"
  },
  sectionDesc: {
    margin: "0 0 20px",
    fontSize: "14px",
    color: "#666"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginTop: "20px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column"
  },
  formGroupFull: {
    display: "flex",
    flexDirection: "column",
    gridColumn: "1 / -1"
  },
  label: {
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#333"
  },
  input: {
    padding: "12px 15px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    transition: "border-color 0.2s",
    outline: "none",
    resize: "vertical"
  },
  colorInputWrapper: {
    display: "flex",
    gap: "10px",
    alignItems: "center"
  },
  colorInput: {
    width: "50px",
    height: "40px",
    padding: "0",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer"
  },
  colorTextInput: {
    flex: 1,
    padding: "10px 15px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    fontFamily: "monospace"
  },
  logoPreview: {
    marginTop: "15px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    textAlign: "center"
  },
  logoImage: {
    maxWidth: "200px",
    maxHeight: "100px",
    objectFit: "contain"
  },
  saveSection: {
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px solid #e9ecef"
  },
  saveButton: {
    padding: "15px 40px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s"
  }
};
